// Regras compartilhadas para a resposta de GET /api/cep/:cep da API de Endereço.
//
// A partir da versão 1.0.2 da API, `data` é SEMPRE um array: em localidades com CEP
// único (ex: 79290-000) existem vários registros com o mesmo número, cada um com
// logradouro/bairro/município diferentes, e a pessoa precisa escolher qual é o dela.
// Até a 1.0.1, `data` era um objeto só. `extrairRegistrosCep` aceita os dois formatos,
// assim o portal não depende da ordem de deploy entre front e API.

// Normaliza `response.data.data` (axios) para uma lista de registros de CEP.
export const extrairRegistrosCep = (response) => {
  const body = response?.data;
  if (!body?.success) return [];

  const dados = body.data;
  if (Array.isArray(dados)) return dados.filter(Boolean);
  return dados ? [dados] : [];
};

// Comparação de texto sem acento/caixa/espaços extras (a API devolve "CENTRO",
// o perfil pode ter "Centro").
const normalizarTexto = (texto) => String(texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

/**
 * Escolhe o registro de CEP a usar, sem nunca chutar:
 * 1. `cepId` informado (registro que a pessoa escolheu na tela) e presente na lista;
 * 2. lista com um único registro;
 * 3. único registro cujo logradouro (e bairro, se ainda houver empate) bate com o
 *    texto informado, para endereços que vieram do perfil sem `cepId`.
 * Qualquer outro caso (ambíguo ou sem resultado) devolve null.
 */
export const escolherRegistroCep = (registros = [], { cepId, logradouro, bairro } = {}) => {
  if (!registros.length) return null;

  if (cepId !== undefined && cepId !== null && cepId !== '') {
    const escolhido = registros.find((r) => String(r.id) === String(cepId));
    if (escolhido) return escolhido;
  }

  if (registros.length === 1) return registros[0];

  let candidatos = registros;
  if (normalizarTexto(logradouro)) {
    candidatos = candidatos.filter((r) => normalizarTexto(r.logradouro) === normalizarTexto(logradouro));
  }
  if (candidatos.length > 1 && normalizarTexto(bairro)) {
    candidatos = candidatos.filter((r) => normalizarTexto(r.bairro) === normalizarTexto(bairro));
  }

  return candidatos.length === 1 ? candidatos[0] : null;
};

// Texto de exibição de uma opção de CEP: "Rua X, Centro — Bonito/MS".
// Logradouro/bairro podem vir vazios em CEP de localidade.
export const rotuloRegistroCep = (registro) => {
  if (!registro) return '';
  const endereco = [registro.logradouro, registro.bairro].filter(Boolean).join(', ');
  const cidade = [registro.cidade, registro.estado].filter(Boolean).join('/');
  return [endereco, cidade].filter(Boolean).join(' — ') || `Registro ${registro.id}`;
};

// Critérios de escolha do registro de CEP (ver escolherRegistroCep) a partir do
// formData, pelo nome do campo de CEP: `{campoCep}Id` é o registro escolhido na tela;
// logradouro/bairro (com o mesmo sufixo do CEP, ex: cepResidencial → ruaResidencial
// ou logradouroResidencial) servem de desempate para endereços vindos do perfil.
export function criteriosCepDoFormulario(formData = {}, campoCep) {
  const sufixo = String(campoCep || '').replace(/^cep/, '');
  return {
    cepId: formData[`${campoCep}Id`],
    logradouro: formData[`logradouro${sufixo}`] || formData[`rua${sufixo}`],
    bairro: formData[`bairro${sufixo}`]
  };
}
